import { CheckCircleIcon, CheckIcon, ListChecksIcon, UserIcon } from '@phosphor-icons/react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { useReportListingOpened } from '@entities/search-run';
import { ButtonLink } from '@shared/ui';

import { useServiceOffer } from '../model/useServiceOffer';

import { PaymentStepAction } from './PaymentStepAction';
import styles from './ServiceChoice.module.css';

/** Что человек делает сам, — пункты макета 04. Коды — ключи i18n. */
const SELF_ITEMS = ['call', 'availability', 'history', 'visit', 'negotiate'] as const;

/**
 * Что делает Karl после оплаты — пункты макета 04.
 *
 * Формулировки «Wir prüfen die interessantesten Fahrzeuge», «Wir überprüfen
 * Historie, Zustand, Unterlagen» и «top geprüfte Fahrzeuge» — тексты макета по
 * решению заказчика от 27.10.2026 (TASKS, Epic 58) и осознанное отступление от
 * CLAUDE.md §2: CarViQ.DE спрашивает автохаус, а не проверяет автомобиль. Ключи
 * перечислены в одном списке исключений теста фраз
 * (`shared/i18n/mockupTextExceptions.ts`); прецедентом для других экранов это
 * не является.
 */
const KARL_ITEMS = ['select', 'clarify', 'check', 'compare', 'top4', 'recommend'] as const;

interface ServiceChoiceProps {
  searchId: string;
  runId: string;
  vehicleId: string;
  /** Объявление на сайте автохауса; `null` — ссылки нет, и «Ich mache das selbst» не рисуется. */
  listingUrl: string | null;
  /** Размер рабочего набора; `undefined` — ещё не известен, пункт с числом не рисуется. */
  setSize: number | undefined;
}

/**
 * Выбор «сам / Karl» на экране автомобиля до оплаты (Epic 58, FE-587, макет 04).
 *
 * Две равные колонки — и это честный выбор, а не воронка с ловушкой: данные
 * автомобилей набора бесплатны, и человек может пойти к автохаусу сам. Платная
 * услуга — работа Karl, а не доступ к объявлениям.
 *
 * * **«Selbst weitermachen (kostenlos)»** — «Ich mache das selbst» синим
 *   контуром открывает объявление на сайте автохауса в новой вкладке, событие
 *   `source_listing_opened` уходит параллельно (BE-582). Заказ при этом не
 *   меняется, оплатить можно в любой момент. Без ссылки кнопки нет — текст
 *   колонки остаётся.
 * * **«Karl übernimmt»** — единственная красная кнопка экрана, к оплате (гостю —
 *   окно входа). На телефоне её несёт полоса оболочки рядом с Karl (FE-585).
 *   Цена — из конфигурации, рядом условия вознаграждения (правило FE-210).
 */
export function ServiceChoice({
  searchId,
  runId,
  vehicleId,
  listingUrl,
  setSize,
}: ServiceChoiceProps) {
  const { t } = useTranslation();
  const reportListingOpened = useReportListingOpened();
  const { price, feeTerms } = useServiceOffer();
  const selfId = useId();
  const karlId = useId();

  return (
    <div className={styles.choice}>
      <section className={`${styles.column} ${styles.self}`} aria-labelledby={selfId}>
        <div className={styles.head}>
          <span className={styles.icon} aria-hidden="true">
            <UserIcon size={24} />
          </span>
          <h2 id={selfId} className={styles.title}>
            {t('showcase.choice.self.title')}
          </h2>
        </div>

        <ul className={styles.list}>
          {setSize === undefined ? null : (
            <li className={styles.item}>
              <CheckIcon size={16} weight="bold" aria-hidden="true" className={styles.check} />
              <span>{t('showcase.choice.self.item.all', { count: setSize })}</span>
            </li>
          )}
          {SELF_ITEMS.map((code) => (
            <li key={code} className={styles.item}>
              <CheckIcon size={16} weight="bold" aria-hidden="true" className={styles.check} />
              <span>{t(`showcase.choice.self.item.${code}`)}</span>
            </li>
          ))}
        </ul>

        {listingUrl === null ? null : (
          <ButtonLink
            to={listingUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="outline-action"
            fullWidth
            className={styles.action}
            onClick={() => {
              reportListingOpened({ runId, vehicleId, via: 'self_service' });
            }}
          >
            {t('showcase.choice.self.action')}
            <span className="visually-hidden"> {t('showcase.dealer.newTab')}</span>
          </ButtonLink>
        )}
      </section>

      <section className={`${styles.column} ${styles.karl}`} aria-labelledby={karlId}>
        <div className={styles.head}>
          <span className={styles.icon} aria-hidden="true">
            <ListChecksIcon size={24} />
          </span>
          <h2 id={karlId} className={styles.title}>
            {t('showcase.choice.karl.title')}
            {price === null ? null : (
              <>
                {' '}
                <span className={`${styles.price} tabular`}>
                  {t('showcase.choice.karl.price', { price })}
                </span>
              </>
            )}
          </h2>
        </div>

        <ul className={styles.list}>
          {KARL_ITEMS.map((code) => (
            <li key={code} className={styles.item}>
              <CheckCircleIcon
                size={16}
                weight="fill"
                aria-hidden="true"
                className={styles.checkKarl}
              />
              <span>{t(`showcase.choice.karl.item.${code}`)}</span>
            </li>
          ))}
        </ul>

        {feeTerms === null ? null : <p className={styles.fee}>{feeTerms}</p>}

        <PaymentStepAction searchId={searchId} className={styles.action}>
          {t('showcase.choice.karl.action')}
        </PaymentStepAction>
      </section>
    </div>
  );
}
